import React, {useCallback, useMemo, useState} from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import SearchIcon from '@mui/icons-material/Search';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import LooksOneIcon from '@mui/icons-material/LooksOne';
import HubIcon from '@mui/icons-material/Hub';
import {MinimalCutSet} from '../../../../utils/minimal-cut-sets';
import {FaultTreeNode} from '../../../../@types/faultTreeNode';

interface ImportanceMeasuresTableProps {
    cutSets: MinimalCutSet[];
    nodeMap: Map<number, FaultTreeNode>;
    nodeNameMap: Map<number, string>;
    totalProbability: number;
    structuralHubIds?: Set<number>;
    onEventClick?: (baseEventId: number) => void;
}

interface ImportanceRow {
    id: number;
    name: string;
    isBarrier: boolean;
    isHub: boolean;
    isSPF: boolean;
    probability: number;
    cutSetCount: number;
    fv: number;
    raw: number;
    rrw: number;
}

type SortField = 'name' | 'probability' | 'cutSetCount' | 'fv' | 'raw' | 'rrw';
type SortDir = 'asc' | 'desc';

function fvColor(val: number): string {
    if (val >= 0.8) return 'rgba(211, 47, 47, 0.85)';
    if (val >= 0.5) return 'rgba(211, 47, 47, 0.5)';
    if (val >= 0.2) return 'rgba(255, 152, 0, 0.4)';
    return 'transparent';
}

function fvTextColor(val: number): string {
    return val >= 0.8 ? '#fff' : '#333';
}

function rawColor(val: number): string {
    if (val >= 100) return 'rgba(211, 47, 47, 0.85)';
    if (val >= 10) return 'rgba(211, 47, 47, 0.5)';
    if (val >= 2) return 'rgba(255, 152, 0, 0.4)';
    return 'transparent';
}

function rawTextColor(val: number): string {
    return val >= 100 ? '#fff' : '#333';
}

function rrwColor(val: number): string {
    if (val >= 10) return 'rgba(46, 125, 50, 0.7)';
    if (val >= 2) return 'rgba(46, 125, 50, 0.4)';
    if (val >= 1.1) return 'rgba(46, 125, 50, 0.2)';
    return 'transparent';
}

function formatValue(val: number): string {
    if (!isFinite(val)) return '∞';
    if (val === 0) return '0';
    if (val >= 1000) return val.toExponential(2);
    if (val >= 10) return val.toFixed(1);
    if (val >= 1) return val.toFixed(2);
    return val.toFixed(3);
}

const ImportanceMeasuresTable: React.FC<ImportanceMeasuresTableProps> = ({
    cutSets, nodeMap, nodeNameMap, totalProbability, structuralHubIds, onEventClick,
}) => {
    const [sortField, setSortField] = useState<SortField>('fv');
    const [sortDir, setSortDir] = useState<SortDir>('desc');
    const [searchText, setSearchText] = useState<string>('');

    const rows = useMemo((): ImportanceRow[] => {
        if (totalProbability === 0) return [];

        // Collect all base event IDs
        const allIds = new Set<number>();
        cutSets.forEach((cs) => cs.baseEventIds.forEach((id) => allIds.add(id)));

        return Array.from(allIds).map((id) => {
            const node = nodeMap.get(id);
            const prob = node?.probability ?? 0;

            // Cut sets containing this event
            const containingCS = cutSets.filter((cs) => cs.baseEventIds.has(id));
            const cutSetCount = containingCS.length;

            // FV: sum of probabilities of cut sets containing this event / total
            const fv = containingCS.reduce((sum, cs) => sum + cs.probability, 0) / totalProbability;

            // RAW: set this event's probability to 1, recompute cut set probabilities
            let rawTotal = 0;
            for (const cs of cutSets) {
                if (cs.baseEventIds.has(id)) {
                    // Recompute: product of other events' probabilities (this one = 1)
                    let p = 1;
                    for (const eid of cs.baseEventIds) {
                        if (eid !== id) {
                            p *= nodeMap.get(eid)?.probability ?? 0;
                        }
                    }
                    rawTotal += p;
                } else {
                    rawTotal += cs.probability;
                }
            }
            const raw = rawTotal / totalProbability;

            // RRW: set this event's probability to 0, all containing cut sets become 0
            const nonContainingTotal = cutSets
                .filter((cs) => !cs.baseEventIds.has(id))
                .reduce((sum, cs) => sum + cs.probability, 0);
            const rrw = nonContainingTotal > 0 ? totalProbability / nonContainingTotal : Infinity;

            return {
                id,
                name: nodeNameMap.get(id) || `ID:${id}`,
                isBarrier: node?.nodeClass === 'BARRIER',
                isHub: structuralHubIds?.has(id) ?? false,
                isSPF: containingCS.some((cs) => cs.baseEventIds.size === 1),
                probability: prob,
                cutSetCount,
                fv,
                raw,
                rrw,
            };
        });
    }, [cutSets, nodeMap, nodeNameMap, totalProbability, structuralHubIds]);

    const sortedRows = useMemo((): ImportanceRow[] => {
        const dir = sortDir === 'asc' ? 1 : -1;
        return [...rows].sort((a, b) => {
            switch (sortField) {
                case 'name': return a.name.localeCompare(b.name) * dir;
                case 'probability': return (a.probability - b.probability) * dir;
                case 'cutSetCount': return (a.cutSetCount - b.cutSetCount) * dir;
                case 'fv': return (a.fv - b.fv) * dir;
                case 'raw': return ((isFinite(a.raw) ? a.raw : 1e12) - (isFinite(b.raw) ? b.raw : 1e12)) * dir;
                case 'rrw': return ((isFinite(a.rrw) ? a.rrw : 1e12) - (isFinite(b.rrw) ? b.rrw : 1e12)) * dir;
                default: return 0;
            }
        });
    }, [rows, sortField, sortDir]);

    const filteredRows = useMemo((): ImportanceRow[] => {
        if (!searchText.trim()) return sortedRows;
        const term = searchText.trim().toLowerCase();
        return sortedRows.filter((row) => row.name.toLowerCase().includes(term));
    }, [sortedRows, searchText]);

    const handleSort = (field: SortField): void => {
        if (sortField === field) {
            setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDir(field === 'name' ? 'asc' : 'desc');
        }
    };

    const handleExportCSV = useCallback((): void => {
        const header = ['Basic Event', 'Probability', 'Cut Sets', 'RC (%)', 'RIF', 'RRF'].join(',');
        const csvRows = filteredRows.map((row) =>
            [
                `"${row.name.replace(/"/g, '""')}"`,
                row.probability === 0 ? '0' : row.probability.toExponential(2),
                row.cutSetCount,
                (row.fv * 100).toFixed(1),
                formatValue(row.raw),
                formatValue(row.rrw),
            ].join(',')
        );
        const csv = [header, ...csvRows].join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', `risk_significance_${new Date().toISOString().slice(0, 10)}.csv`);
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }, [filteredRows]);

    if (totalProbability === 0) {
        return (
            <Box sx={{ px: 2, py: 2, borderBottom: 1, borderColor: 'divider', textAlign: 'center' }}>
                <Typography variant="body2" color="error">
                    Risk significance measures require non-zero base event probabilities.
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 2, pt: 1 }}>
                <Typography variant="subtitle2" sx={{ flexShrink: 0 }}>Risk Significance</Typography>
                <TextField
                    size="small"
                    placeholder="Search events"
                    value={searchText}
                    onChange={(e): void => setSearchText(e.target.value)}
                    slotProps={{
                        input: {
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            ),
                        },
                    }}
                    sx={{ minWidth: 150, ml: 'auto' }}
                />
                <Tooltip title="Export to CSV">
                    <IconButton size="small" onClick={handleExportCSV}>
                        <FileDownloadIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Stack>
            <TableContainer component={Paper} elevation={0} sx={{ maxHeight: 350, overflow: 'auto' }}>
                <Table stickyHeader size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>
                                <TableSortLabel
                                    active={sortField === 'name'}
                                    direction={sortField === 'name' ? sortDir : 'asc'}
                                    onClick={(): void => handleSort('name')}
                                >
                                    Basic Event
                                </TableSortLabel>
                            </TableCell>
                            <TableCell align="right">
                                <TableSortLabel
                                    active={sortField === 'probability'}
                                    direction={sortField === 'probability' ? sortDir : 'desc'}
                                    onClick={(): void => handleSort('probability')}
                                >
                                    Probability
                                </TableSortLabel>
                            </TableCell>
                            <TableCell align="right">
                                <TableSortLabel
                                    active={sortField === 'cutSetCount'}
                                    direction={sortField === 'cutSetCount' ? sortDir : 'desc'}
                                    onClick={(): void => handleSort('cutSetCount')}
                                >
                                    <Tooltip title="Number of cut sets containing this event" arrow>
                                        <span>Cut Sets</span>
                                    </Tooltip>
                                </TableSortLabel>
                            </TableCell>
                            <TableCell align="right">
                                <TableSortLabel
                                    active={sortField === 'fv'}
                                    direction={sortField === 'fv' ? sortDir : 'desc'}
                                    onClick={(): void => handleSort('fv')}
                                >
                                    <Tooltip title="Risk Contribution: fraction of top event probability involving this event. Higher = removing this event has more impact." arrow>
                                        <span>RC</span>
                                    </Tooltip>
                                </TableSortLabel>
                            </TableCell>
                            <TableCell align="right">
                                <TableSortLabel
                                    active={sortField === 'raw'}
                                    direction={sortField === 'raw' ? sortDir : 'desc'}
                                    onClick={(): void => handleSort('raw')}
                                >
                                    <Tooltip title="Risk Increase Factor: how much worse does the system get if this event is guaranteed to fail (P=1). Higher = more critical." arrow>
                                        <span>RIF</span>
                                    </Tooltip>
                                </TableSortLabel>
                            </TableCell>
                            <TableCell align="right">
                                <TableSortLabel
                                    active={sortField === 'rrw'}
                                    direction={sortField === 'rrw' ? sortDir : 'desc'}
                                    onClick={(): void => handleSort('rrw')}
                                >
                                    <Tooltip title="Risk Reduction Factor: how much better does the system get if this event is made perfectly reliable (P=0). Higher = more benefit from improvement." arrow>
                                        <span>RRF</span>
                                    </Tooltip>
                                </TableSortLabel>
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredRows.map((row) => (
                            <TableRow
                                key={row.id}
                                hover
                                onClick={onEventClick ? (): void => onEventClick(row.id) : undefined}
                                sx={onEventClick ? { cursor: 'pointer' } : undefined}
                            >
                                <TableCell>
                                    <Stack direction="row" alignItems="center" spacing={0.5}>
                                        {row.isSPF && (
                                            <Tooltip title="Single point of failure" arrow>
                                                <LooksOneIcon sx={{ fontSize: 16, color: 'error.main' }} />
                                            </Tooltip>
                                        )}
                                        {row.isHub && (
                                            <Tooltip title="Structural hub (appears in ≥50% of cut sets)" arrow>
                                                <HubIcon sx={{ fontSize: 14, color: '#ed6c02' }} />
                                            </Tooltip>
                                        )}
                                        <span style={row.isBarrier ? { color: 'green', fontWeight: 600 } : undefined}>
                                            {row.name}
                                        </span>
                                    </Stack>
                                </TableCell>
                                <TableCell align="right">{row.probability === 0 ? '0' : row.probability.toExponential(2)}</TableCell>
                                <TableCell align="right">{row.cutSetCount}</TableCell>
                                <TableCell
                                    align="right"
                                    sx={{ backgroundColor: fvColor(row.fv), color: fvTextColor(row.fv) }}
                                >
                                    {(row.fv * 100).toFixed(1)}%
                                </TableCell>
                                <TableCell
                                    align="right"
                                    sx={{ backgroundColor: rawColor(row.raw), color: rawTextColor(row.raw) }}
                                >
                                    {formatValue(row.raw)}
                                </TableCell>
                                <TableCell
                                    align="right"
                                    sx={{ backgroundColor: rrwColor(row.rrw) }}
                                >
                                    {formatValue(row.rrw)}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

export default ImportanceMeasuresTable;
